import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Centralized hook for user preferences (region, providers, onboarded).
 * When logged in, reads/writes from DB (profiles table) and keeps localStorage in sync.
 * When logged out, uses localStorage only.
 */
export const usePreferences = () => {
  const { user } = useAuth();

  const getRegion = useCallback(async (): Promise<string> => {
    if (user) {
      const { data } = await (supabase.from("profiles") as any)
        .select("region")
        .eq("id", user.id)
        .single();
      if (data?.region) {
        localStorage.setItem("ayeris_region", data.region);
        return data.region;
      }
    }
    return localStorage.getItem("ayeris_region") || "GB";
  }, [user]);

  const setRegion = useCallback(async (region: string) => {
    localStorage.setItem("ayeris_region", region);
    if (user) {
      await (supabase.from("profiles") as any)
        .update({ region })
        .eq("id", user.id);
    }
  }, [user]);

  const getProviders = useCallback(async (): Promise<number[]> => {
    if (user) {
      const { data } = await (supabase.from("profiles") as any)
        .select("providers")
        .eq("id", user.id)
        .single();
      if (data?.providers && Array.isArray(data.providers) && data.providers.length > 0) {
        localStorage.setItem("ayeris_providers", JSON.stringify(data.providers));
        return data.providers;
      }
    }
    try {
      return JSON.parse(localStorage.getItem("ayeris_providers") || "[]");
    } catch { return []; }
  }, [user]);

  const setProviders = useCallback(async (providers: number[]) => {
    localStorage.setItem("ayeris_providers", JSON.stringify(providers));
    if (user) {
      await (supabase.from("profiles") as any)
        .update({ providers })
        .eq("id", user.id);
    }
  }, [user]);

  const getOnboarded = useCallback(async (): Promise<boolean> => {
    if (user) {
      const { data } = await (supabase.from("profiles") as any)
        .select("onboarded")
        .eq("id", user.id)
        .single();
      const onboarded = !!data?.onboarded;
      if (onboarded) {
        localStorage.setItem("ayeris_onboarded", "true");
      }
      // When authenticated, DB is the sole source of truth
      return onboarded;
    }
    return localStorage.getItem("ayeris_onboarded") === "true";
  }, [user]);

  const setOnboarded = useCallback(async (val: boolean) => {
    if (val) {
      localStorage.setItem("ayeris_onboarded", "true");
    } else {
      localStorage.removeItem("ayeris_onboarded");
    }
    if (user) {
      await (supabase.from("profiles") as any)
        .update({ onboarded: val })
        .eq("id", user.id);
    }
  }, [user]);

  /** Save all onboarding preferences at once (region + providers + onboarded flag) */
  const saveOnboardingPrefs = useCallback(async (region: string, providers: number[]) => {
    localStorage.setItem("ayeris_region", region);
    localStorage.setItem("ayeris_providers", JSON.stringify(providers));
    localStorage.setItem("ayeris_onboarded", "true");
    if (user) {
      await (supabase.from("profiles") as any)
        .update({ region, providers, onboarded: true })
        .eq("id", user.id);
    }
  }, [user]);

  /** Migrate localStorage prefs to DB on first login */
  const migratePrefsToDb = useCallback(async () => {
    if (!user) return;
    const region = localStorage.getItem("ayeris_region");
    const providers = localStorage.getItem("ayeris_providers");
    const onboarded = localStorage.getItem("ayeris_onboarded") === "true";
    if (region || providers || onboarded) {
      const update: any = { onboarded };
      if (region) update.region = region;
      if (providers) {
        try { update.providers = JSON.parse(providers); } catch {}
      }
      await (supabase.from("profiles") as any)
        .update(update)
        .eq("id", user.id);
    }
  }, [user]);

  return {
    getRegion, setRegion,
    getProviders, setProviders,
    getOnboarded, setOnboarded,
    saveOnboardingPrefs,
    migratePrefsToDb,
  };
};
